<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <div class="d-flex justify-content-between align-items-center">
        <h1 class="page-title">臨時業務情報一覧</h1>
        <div>
          <button @click="exportToExcel" class="btn btn-success">Excel出力</button>
          <router-link :to="{ name: 'Home' }">
            <button type="button" class="btn btn-primary mx-1">ホーム</button>
          </router-link>
        </div>
      </div>
  
      <div class="mb-2 d-flex justify-content-between align-items-center">
        <div>
          <router-link :to="{ 
            name: 'TemporaryWorkCreate', 
            params: { year: targetYear, month: targetMonth },
            query: getSearchQuery() 
          }">
            <button type="button" class="btn btn-sm btn-primary mx-1">臨時作業情報作成</button>
          </router-link>

          <button class="btn btn-sm btn-outline-secondary" @click="isSearchExpanded = !isSearchExpanded">
            {{ isSearchExpanded ? '検索条件を非表示' : '検索条件を表示' }}
          </button>
        </div>

        <div class="temp-total-amount-area">
          <table class="table table-bordered mb-0">
            <tr>
              <th>売上金額<br><small>(税抜)</small></th>
              <td>{{ formatCurrency(totalOrderAmount) }}</td>
              <th>支払金額<br><small>(税抜)</small></th>
              <td>{{ formatCurrency(totalRequestAmount) }}</td>
              <th>粗利率(%)</th>
              <td>{{ totalGrossProfitMargin }}%</td>
            </tr>
          </table>
          <small class="text-muted">請求年月と現在の検索条件に一致する全件の合計（全ページ対象）</small>
        </div>
      </div>
  
      <!-- 検索バーの追加 -->
      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value" >
        <form class="search-container" style="background-color: #f8f9fa;" @submit.prevent="searchTemporaryWorks"> <!-- 背景色を薄く設定 -->
          <div class="d-flex align-items-center flex-wrap">

            <!-- 請求番号検索 -->
            <div class="d-flex align-items-center my-2 me-3">
              <div class="me-2">
                <label for="searchBillingNo" class="col-form-label">請求ID</label>
              </div>
              <div class="col-auto">
                <input type="text" id="searchBillingNo" class="form-control" v-model="searchBillingNo" placeholder="請求番号で検索">
              </div>
            </div>

            <!-- 年月FromTo -->
            <div class="d-flex align-items-center my-2 me-3">
              <label class="col-form-label me-2">請求年月</label>

              <!-- From -->
              <div class="d-flex align-items-center me-2">
                <select class="form-select me-1" v-model="searchYearFrom" style="min-width: 120px">
                  <option value="">年</option>
                  <!-- フィルタなしで全年度を表示 -->
                  <option v-for="year in years" :key="'from-' + year" :value="year">{{ year }}年</option>
                </select>

                <select class="form-select" v-model="searchMonthFrom" style="min-width: 100px">
                  <option value="">月</option>
                  <!-- フィルタなしで全月を表示 -->
                  <option v-for="month in months" :key="'from-month-' + month" :value="month">{{ month }}月</option>
                </select>
              </div>

              <label class="col-form-label me-2">〜</label>

              <!-- To -->
              <div class="d-flex align-items-center">
                <select class="form-select me-1" v-model="searchYearTo" style="min-width: 120px">
                  <option value="">年</option>
                  <!-- ToはfilteredYearsToで制御 -->
                  <option v-for="year in filteredYearsTo" :key="'to-' + year" :value="year">{{ year }}年</option>
                </select>

                <select class="form-select" v-model="searchMonthTo" style="min-width: 100px">
                  <option value="">月</option>
                  <!-- ToはfilteredMonthsToで制御 -->
                  <option v-for="month in filteredMonthsTo" :key="'to-month-' + month" :value="month">{{ month }}月</option>
                </select>
              </div>
            </div>

            <!-- 顧客検索 -->
            <div class="d-flex align-items-center my-2 me-3">
              <div class="col-auto">
                <CustomerSelectBox
                  v-model="searchCustomerName"
                  :options="customers"
                  placeholder="顧客名で検索"
                  :formatter="c => c.customer_nm"
                />
              </div>
            </div>

            <!-- 建物検索 -->
            <div class="d-flex align-items-center my-2 me-3">
              <div class="col-auto">
                <BuildingSelectBox
                  v-model="searchBuildingName"
                  :options="buildings"
                  placeholder="建物名で検索"
                  :formatter="b => b.building_nm"
                />
              </div>
            </div>

            <!-- 請負業者検索 -->
            <div class="d-flex align-items-center my-2 me-3">
              <div class="col-auto">
                <VendorSelectBox
                  v-model="searchVendorName"
                  :options="vendors"
                  placeholder="業者名で検索"
                  :formatter="v => v.vendor_nm"
                />
              </div>
            </div>

            <!-- 担当者検索 -->
            <div class="d-flex align-items-center my-2 me-3">
              <div class="col-auto">
                <UserSelectBox
                  v-model="searchUserName"
                  :options="users"
                  placeholder="担当者名で検索"
                  :formatter="v => v.name"
                />
              </div>
            </div>

            <!-- 作業内容検索 -->
            <div class="d-flex align-items-center my-2 me-3">
              <div class="col-auto">
                <input type="text" id="searchWorkContent" class="form-control" v-model="searchWorkContent" placeholder="作業内容で検索">
              </div>
            </div>

            <!-- 請求書発行フラグ -->
            <div class="d-flex align-items-center me-2">
              <select class="form-select me-1" v-model="searchBillingFlg">
                <option value="" selected>全て</option>
                <option value="1">請求書作成済み</option>
                <option value="0">請求書未発行</option>
              </select>
            </div>

            <div class="col-auto my-2">
              <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
              <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetTemporaryWorks">クリア</button>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-between align-items-end" ref="paginationContainer">
      <p class="text-danger mb-1">※受注日、作業日をクリックすると表示の並び替えができます</p>
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="btn-cell th-sticky sticky-left"></th>
            <th class="text-nowrap th-sticky sticky-left-second">実施確認</th>
            <th class="text-nowrap th-sticky sticky-left-third">請求ID</th>
            <th class="text-nowrap" @click="toggleOrderSortOrder" style="cursor: pointer;">
              受注日
              <span v-if="orderSortOrder === 'asc'">▲</span>
              <span v-else>▼</span>
            </th>
            <th class="text-nowrap" @click="toggleSortOrder" style="cursor: pointer;">
              作業日
              <span v-if="workSortOrder === 'asc' || workSortOrder === ''">▲</span>
              <span v-else-if="workSortOrder === 'desc'">▼</span>
            </th>
            <th class="text-nowrap">見積書No.</th>
            <th class="text-nowrap">建物名称</th>
            <th class="text-nowrap">業務区分</th>
            <th class="text-nowrap">業務内容</th>
            <th class="text-nowrap">内訳</th>
            <th class="text-nowrap">顧客名(請求先)</th>
            <th class="text-nowrap">顧客<br>担当者</th>
            <th class="text-nowrap">受注金額(税抜)</th>
            <th class="text-nowrap">発注業者/発注金額(税抜)</th>
            <th class="text-nowrap">総務チェック<br>(業者請求確認)</th>
            <th class="text-nowrap">業者請求確認日</th>
            <th class="text-nowrap">粗利(%)</th>
            <th class="text-nowrap">当社<br>担当者</th>
            <th class="text-nowrap">請求年月</th>
            <th class="text-nowrap">請求発行依頼<br>(担当者記入)</th>
            <th class="text-nowrap">総務チェック<br>(請求確認)</th>
            <th class="text-nowrap">総務請求確認日</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="temporaryWork in temporaryWorks"
            :key="temporaryWork.id"
            :class="{ 'unworked-row': temporaryWork.unworked_flg }"
          >
            <td class="btn-cell text-nowrap th-sticky sticky-left">
              <router-link
                :to="{
                  name: 'TemporaryWorkDetail',
                  params: { id: temporaryWork.id },
                  query: getSearchQuery()
                }"
              >
                <button type="button" class="btn btn-sm btn-info">詳細</button>
              </router-link>

              <router-link
                v-if="isAdmin || temporaryWork.user_id == userId"
                :to="{
                  name: 'TemporaryWorkEdit',
                  params: { id: temporaryWork.id },
                  query: getSearchQuery()
                }"
              >
                <button type="button" class="btn btn-sm btn-warning mx-2">編集</button>
              </router-link>

              <button
                v-else
                type="button"
                class="btn btn-sm btn-warning mx-2"
                disabled
              >
                編集
              </button>

              <button
                type="button"
                class="btn btn-sm btn-secondary me-2"
                @click="handleCopyClick(temporaryWork)"
              >
                コピー
              </button>

              <!-- 管理者または本人のみ削除可能 -->
              <button
                v-if="isAdmin || temporaryWork.user_id == userId"
                class="btn btn-sm btn-danger"
                data-bs-toggle="modal"
                data-bs-target="#deleteModal"
                @click="handleDeleteClick(temporaryWork.id)"
              >
                削除
              </button>

              <!-- それ以外のユーザーには非活性状態を表示 -->
              <button
                v-else
                class="btn btn-sm btn-danger"
                disabled
              >
                削除
              </button>
            </td>
            <td class="text-nowrap th-sticky sticky-left-second text-center">
              <!-- 作業実施確認ボタン -->
              <div v-if="temporaryWork.unworked_flg">
                <template v-if="isAdmin || temporaryWork.user_id == userId">
                  <button
                    class="btn btn-sm btn-warning mx-1"
                    @click="openWorkUnworkedCancelModal(temporaryWork.id, targetYear, targetMonth)"
                  >
                    未実施取消
                  </button>
                </template>
                <template v-else>
                  <span class="text-muted">未実施</span>
                </template>
              </div>
              <div v-else-if="!(temporaryWork.confirm_worked)">
                <div v-if="isAdmin || temporaryWork.user_id == userId">
                  <button class="btn btn-sm btn-success mx-1"
                          @click="openWorkCompleteCheckModal(temporaryWork.id, year, month, temporaryWork.work_dt)">
                    実施登録
                  </button>
                </div>
              </div>
              <div v-else>
                <template v-if="isAdmin">
                  <button class="btn btn-sm btn-warning mx-1"
                          @click="openWorkCompleteCancelModal(temporaryWork.id, year, month)">
                    実施解除
                  </button>
                </template>
                <template v-else>
                  <span class="text-muted">実施済み</span>
                </template>
              </div>
            </td>
            <td class="text-nowrap th-sticky sticky-left-third">
              {{ temporaryWork.billing_no }}
              <span v-if="temporaryWork.billing_flg" class="billing-complete">✅</span>
            </td>
            <td class="text-nowrap">{{ formatYearMonthDay(temporaryWork.order_dt) }}</td>
            <td class="text-nowrap">{{ formatYearMonthDay(temporaryWork.work_dt) }}</td>
            <td class="text-nowrap">{{ temporaryWork.quotion_no }}</td>
            <td class="text-nowrap">{{ temporaryWork.m_building?.building_nm }}</td>
            <td class="text-nowrap">{{ temporaryWork.m_business_classification?.business_classification_nm }}</td>
            <td class="multiline-text">{{ temporaryWork.work_content }}</td>
            <td class="multiline-text">{{ temporaryWork.breakdown }}</td>
            <td class="multiline-text">{{ temporaryWork.m_customer?.customer_nm }}</td>
            <td class="text-nowrap">
              {{ temporaryWork.m_customer_representative?.in_charge_last_nm }} 
              {{ temporaryWork.m_customer_representative?.in_charge_first_nm }}
            </td>
            <td class="text-nowrap">{{ formatCurrency(temporaryWork.order_amount) }}</td>
            <!-- 自社で作業（クラスなし、シンプル表示） -->
            <td v-if="temporaryWork.is_in_house" class="text-nowrap">
              自社で作業
            </td>

            <!-- 業者がいる -->
            <td v-else-if="temporaryWork.t_temporary_work_vendors?.length > 0" class="text-nowrap p-0">
              <table class="vendor-info-table">
                <tbody>
                  <tr v-for="vendor in temporaryWork.t_temporary_work_vendors" :key="vendor.id">
                    <th class="text-wrap">{{ vendor.m_vendor?.vendor_nm }}</th>
                    <td class="text-nowrap text-end">{{ formatCurrency(vendor.request_amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </td>

            <!-- 業者もいない -->
            <td v-else>
              業者情報なし
            </td>

            <td class="text-nowrap">
              <!-- 総務チェック（業者請求確認）ボタン -->
              <div v-if="temporaryWork.t_temporary_work_vendors?.length">
                <div v-if="!(temporaryWork.confirm_vendor_billing)">
                  <button class="btn btn-sm btn-success mx-1"
                          @click="openGeneralAffairsVendorCheckModal(temporaryWork.id, year, month)">
                    業者請求確認登録
                  </button>
                </div>
                <div v-else>
                  <template v-if="isAdmin">
                    <button class="btn btn-sm btn-warning mx-1"
                            @click="openGeneralAffairsVendorCancelModal(temporaryWork.id, year, month)">
                      業者請求確認解除
                    </button>
                  </template>
                  <template v-else>
                    <span class="text-muted">確認済み</span>
                  </template>
                </div>
              </div>
              <div v-else class="text-center">
                -
              </div>
            </td>
            <td class="text-nowrap">
              <div v-if="temporaryWork.t_temporary_work_vendors?.length">
                {{ formatYearMonthDay(temporaryWork.vendor_billing_confirm_dt) }}
              </div>
              <div v-else class="text-center">
                -
              </div>
            </td>
            <td class="text-nowrap">{{ temporaryWork.gross_profit_margin }}%</td>
            <td class="text-nowrap">{{ temporaryWork.user?.name }}</td>
            <td class="text-nowrap">
              <div v-if="temporaryWork.billing_year && temporaryWork.billing_month">
                {{ temporaryWork.billing_year }}年{{ temporaryWork.billing_month }}月
              </div>
            </td>
            <td class="text-nowrap">{{ temporaryWork.billing_request }}</td>
            <td class="text-nowrap">
              <!-- 総務チェック（請求確認）ボタン -->
              <div v-if="!(temporaryWork.confirm_billing)">
                未確認
              </div>
              <div v-else>
                確認済み
              </div>
            </td>
            <td class="text-nowrap">{{ formatYearMonthDay(temporaryWork.confirm_billing_dt) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 削除確認モーダル -->
    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

    <!-- 総務チェック（業者請求確認）モーダル -->
    <GeneralAffairsVendorCheckModal ref="generalAffairsVendorCheckModal" @onClose="fetchTemporaryWorks" />

    <!-- 総務チェック（業者請求確認）解除モーダル -->
    <GeneralAffairsVendorCancelModal ref="generalAffairsVendorCancelModal" @onClose="fetchTemporaryWorks" />

    <!-- 作業実施確認モーダル -->
    <WorkCompleteCheckModal ref="workCompleteCheckModal" @onClose="fetchTemporaryWorks" />

    <!-- 作業実施解除モーダル -->
    <WorkCompleteCancelModal ref="workCompleteCancelModal" @onClose="fetchTemporaryWorks" />

    <!-- 作業未実施取消モーダル -->
    <WorkUnworkedCancelModal ref="workUnworkedCancelModal" @onClose="fetchTemporaryWorks" />
  </div>
</template>

<script>
import { ref, onMounted, watch, watchEffect, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import * as XLSX from 'xlsx'; // SheetJSをインポート
import {
  getTemporaryWorks,
  getSelectBoxCustomers,
  getSelectBoxBuildings,
  getSelectBoxVendors,
  getSelectBoxUsers,
  deleteTemporaryWork
} from '@/api/axios';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import BuildingSelectBox from '@/components/searchSelectBoxes/BuildingSelectBox.vue';
import VendorSelectBox from '@/components/searchSelectBoxes/VendorSelectBox.vue';
import UserSelectBox from '@/components/searchSelectBoxes/UserSelectBox.vue';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import GeneralAffairsVendorCheckModal from './GeneralAffairsVendorCheckModal.vue'; // 同じ階層のモーダルをインポート
import GeneralAffairsVendorCancelModal from './GeneralAffairsVendorCancelModal.vue'; // 同じ階層のモーダルをインポート
import WorkCompleteCheckModal from './WorkCompleteCheckModal.vue'; // 同じ階層のモーダルをインポート
import WorkCompleteCancelModal from './WorkCompleteCancelModal.vue'; // 同じ階層のモーダルをインポート
import WorkUnworkedCancelModal from './WorkUnworkedCancelModal.vue'; // 同じ階層のモーダルをインポート
import Pagination from '@/components/shared/Pagination.vue';

export default {
  components: {
    CustomerSelectBox,
    BuildingSelectBox,
    VendorSelectBox,
    UserSelectBox,
    SearchPanelWrapper,
    DeleteConfirmationModal,
    GeneralAffairsVendorCheckModal,
    GeneralAffairsVendorCancelModal,
    WorkCompleteCheckModal,
    WorkCompleteCancelModal,
    WorkUnworkedCancelModal,
    Pagination
  },
  setup() {
    const title = ref('臨時作業情報一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const userId = localStorage.getItem('userId');
    const userRole = computed(() => store.state.userRole);
    const isAdmin = computed(() => userRole.value === '999' || userRole.value === '0');
    const router = useRouter();
    const route = useRoute();
    const customers = ref([]);
    const buildings = ref([]);
    const vendors = ref([]);
    const users = ref([]);
    const temporaryWorks = ref([]);
    const totalOrderAmount = ref('');
    const totalRequestAmount = ref('');
    const totalGrossProfitMargin = ref('');
    const years = ref([]);
    const months = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    const targetYear = ref('');
    const targetMonth = ref('');

    const generalAffairsVendorCheckModal = ref(null); // モーダル用のref
    const generalAffairsVendorCancelModal = ref(null); // モーダル用のref
    const workCompleteCheckModal = ref(null); // モーダル用のref
    const workCompleteCancelModal = ref(null); // モーダル用のref
    const workUnworkedCancelModal = ref(null); // モーダル用のref

    const errorMessages = ref({}); // エラーメッセージ用のリアクティブプロパティ

    // 削除モーダルへ渡すID
    const paramsIdToDelete = ref(null);

    // 現在の年月を取得
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.value.push(i);
    }

    targetYear.value = route.query.year ? parseInt(route.query.year, 10) : '';
    targetMonth.value = route.query.month ? parseInt(route.query.month, 10) : '';

    const isSearchExpanded = ref(true);

    // 検索用のデータプロパティ
    const searchBillingNo = ref('');
    const searchYearFrom = ref('');
    const searchMonthFrom = ref('');
    const searchYearTo = ref('');
    const searchMonthTo = ref('');
    const searchCustomerName = ref('');
    const searchBuildingName = ref('');
    const searchVendorName = ref('');
    const searchUserName = ref('');
    const searchWorkContent = ref('');
    const searchBillingFlg = ref('');
    // 受注日/作業日の並び順を個別に管理
    const orderSortOrder = ref('asc');
    const workSortOrder = ref('');

    // ▼▼ 共通：検索条件をそのまま query として返す ▼▼
    const getSearchQuery = () => ({
      temp_billing_no: searchBillingNo.value,
      temp_year_from: searchYearFrom.value,
      temp_month_from: searchMonthFrom.value,
      temp_year_to: searchYearTo.value,
      temp_month_to: searchMonthTo.value,
      temp_customer_id: searchCustomerName.value,
      temp_building_id: searchBuildingName.value,
      temp_vendor_id: searchVendorName.value,
      temp_user_id: searchUserName.value,
      temp_work_content: searchWorkContent.value,
      temp_billing_flg: searchBillingFlg.value,
      temp_order_sort_order: orderSortOrder.value,
      temp_work_sort_order: workSortOrder.value,
      temp_page: currentPage.value || 1
    });

    // 会計年度は7月〜翌年6月
    if (currentMonth >= 7) {
      // 7月以降 → 当年7月〜翌年6月
      searchYearFrom.value = currentYear;
      searchMonthFrom.value = 7;
      searchYearTo.value = currentYear + 1;
      searchMonthTo.value = 6;
    } else {
      // 1〜6月 → 前年7月〜当年6月
      searchYearFrom.value = currentYear - 1;
      searchMonthFrom.value = 7;
      searchYearTo.value = currentYear;
      searchMonthTo.value = 6;
    }

    // ▼▼▼ ここから年月From/To制御ロジック ▼▼▼
    const ymToNumber = (y, m) => {
      if (!y || !m) return null
      return Number(y) * 100 + Number(m)
    }

    // From変更時 → 毎回 To を同じに設定
    watch([searchYearFrom, searchMonthFrom], ([newYearFrom, newMonthFrom]) => {
      if (!newYearFrom || !newMonthFrom) return
      searchYearTo.value = newYearFrom
      searchMonthTo.value = newMonthFrom
    })

    // To側の制御
    const filteredYearsTo = computed(() => {
      if (!searchYearFrom.value) return years.value
      return years.value.filter((y) => Number(y) >= Number(searchYearFrom.value))
    })

    const filteredMonthsTo = computed(() => {
      if (!searchYearFrom.value || !searchMonthFrom.value) return months.value
      if (searchYearTo.value > searchYearFrom.value) return months.value
      if (searchYearTo.value === searchYearFrom.value)
        return months.value.filter((m) => m >= Number(searchMonthFrom.value))
      return months.value
    })

    // 不正な組み合わせを自動修正（From > Toの場合）
    watch([searchYearFrom, searchMonthFrom, searchYearTo, searchMonthTo], () => {
      const from = ymToNumber(searchYearFrom.value, searchMonthFrom.value)
      const to = ymToNumber(searchYearTo.value, searchMonthTo.value)
      if (from && to && from > to) {
        searchYearTo.value = searchYearFrom.value
        searchMonthTo.value = searchMonthFrom.value
      }
    })
    // ▲▲▲ ここまで年月From/To制御ロジック ▲▲▲

    const toggleOrderSortOrder = () => {
      orderSortOrder.value = orderSortOrder.value === 'asc' ? 'desc' : 'asc';
      workSortOrder.value = '';
      fetchTemporaryWorks(currentPage.value);
    };

    const toggleSortOrder = () => {
      workSortOrder.value = workSortOrder.value === 'asc' ? 'desc' : 'asc';
      fetchTemporaryWorks(currentPage.value);
    };

    const handleCopyClick = (temporaryWork) => {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const todayDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const copyData = {
        quotion_no: temporaryWork.quotion_no,
        m_building_id: temporaryWork.m_building_id,
        m_customer_id: temporaryWork.m_customer_id,
        m_business_classification_id: temporaryWork.m_business_classification_id,
        m_customer_representative_id: temporaryWork.m_customer_representative_id,
        work_content: temporaryWork.work_content,
        breakdown: temporaryWork.breakdown,
        order_dt: todayDate,
        order_amount: '',
        work_dt: '',
        user_id: temporaryWork.user_id,
        billing_year: currentYear,
        billing_month: currentMonth,
        is_in_house: temporaryWork.is_in_house,
        t_temporary_work_vendors: (temporaryWork.t_temporary_work_vendors || []).map((vendor) => ({
          m_vendor_id: vendor.m_vendor_id,
          request_amount: '',
          sort_no: vendor.sort_no
        }))
      };

      sessionStorage.setItem('temporaryWorkCopyData', JSON.stringify(copyData));

      router.push({
        name: 'TemporaryWorkCreate',
        query: getSearchQuery()
      });
    };

    // ページネーション
    const currentPage = ref(1);
    const totalPages = ref(50); // トータルページ数を管理するためのプロパティ

    const fetchTemporaryWorks = async (page = 1) => {
      try {
        const response = await getTemporaryWorks({
          billing_no: searchBillingNo.value,
          year_from: searchYearFrom.value,
          month_from: searchMonthFrom.value,
          year_to: searchYearTo.value,
          month_to: searchMonthTo.value,
          customer_id: searchCustomerName.value,
          building_id: searchBuildingName.value,
          vendor_id: searchVendorName.value,
          user_id: searchUserName.value,
          work_content: searchWorkContent.value,
          billing_flg: searchBillingFlg.value,
          order_sort_order: orderSortOrder.value,
          work_sort_order: workSortOrder.value,
          page: page
        });
        temporaryWorks.value = response.data.temporary_works;
        totalPages.value = response.data.meta.total_pages;
        currentPage.value = response.data.meta.current_page;
        totalOrderAmount.value = response.data.total_order_amount;
        totalRequestAmount.value = response.data.total_request_amount;
        totalGrossProfitMargin.value = response.data.total_gross_profit_margin;
      } catch (error) {
        console.error('臨時作業情報ーデータの取得に失敗しました:', error);
      }

      // 顧客情報（検索用）
      try {
        const response = await getSelectBoxCustomers();
        customers.value = response.data;
      } catch (error) {
        console.error('顧客情報ーデータの取得に失敗しました:', error);
      }

      // 建物情報（検索用）
      try {
        const response = await getSelectBoxBuildings();
        buildings.value = response.data;
      } catch (error) {
        console.error('建物情報ーデータの取得に失敗しました:', error);
      }

      // 業者情報（検索用）
      try {
        const response = await getSelectBoxVendors();
        vendors.value = response.data;
      } catch (error) {
        console.error('業者情報ーデータの取得に失敗しました:', error);
      }

      // ユーザ情報（検索用）
      try {
        const response = await getSelectBoxUsers();
        users.value = response.data;
      } catch (error) {
        console.error('ユーザ情報ーデータの取得に失敗しました:', error);
      }
    };

    // ▼▼▼ 修正：ページ変更時も URL を同期 ▼▼▼
    const handleChangePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        fetchTemporaryWorks(page);

        const q = getSearchQuery();
        q.temp_page = page;

        router.replace({
          path: '/temporary-works',
          query: q
        });

        if (tableWrapper.value) {
          tableWrapper.value.scrollTop = 0;
        }
      }
    };

    // 検索処理
    // ▼▼▼ 修正：検索ボタン押下時にフルクエリを push ▼▼▼
    const searchTemporaryWorks = async () => {
      await fetchTemporaryWorks(1);

      const q = getSearchQuery();
      q.temp_page = 1;

      router.push({
        path: '/temporary-works',
        query: q
      });
    };

    // リセット処理
    const searchResetTemporaryWorks = async () => {
      searchBillingNo.value = '';
      searchYearFrom.value = '';
      searchMonthFrom.value = '';
      searchYearTo.value = '';
      searchMonthTo.value = '';
      searchCustomerName.value = '';
      searchBuildingName.value = '';
      searchVendorName.value = '';
      searchUserName.value = '';
      searchWorkContent.value = '';
      searchBillingFlg.value = '';
      orderSortOrder.value = 'asc';
      workSortOrder.value = '';

      await fetchTemporaryWorks(1);

      const q = getSearchQuery();
      q.temp_page = 1;

      router.replace({
        path: '/temporary-works',
        query: q
      });
    };

    // 総務チェック（業者請求確認）モーダル表示
    const openGeneralAffairsVendorCheckModal = (temporaryWorkId, year, month) => {
      if (generalAffairsVendorCheckModal.value) {
        generalAffairsVendorCheckModal.value.show({ temporaryWorkId, year, month });
      }
    };

    // 総務チェック（業者請求確認）解除モーダル表示
    const openGeneralAffairsVendorCancelModal = (temporaryWorkId, year, month) => {
      if (generalAffairsVendorCancelModal.value) {
        generalAffairsVendorCancelModal.value.show({ temporaryWorkId, year, month });
      }
    };

    // 作業実施確認モーダル表示
    const openWorkCompleteCheckModal = (temporaryWorkId, year, month, workDt) => {
      if (workCompleteCheckModal.value) {
        workCompleteCheckModal.value.show({ temporaryWorkId, year, month, workDt });
      }
    };

    // 作業実施解除モーダル表示
    const openWorkCompleteCancelModal = (temporaryWorkId, year, month) => {
      if (workCompleteCancelModal.value) {
        workCompleteCancelModal.value.show({ temporaryWorkId, year, month });
      }
    };

    // 作業未実施取消モーダル表示
    const openWorkUnworkedCancelModal = (temporaryWorkId, year, month) => {
      if (workUnworkedCancelModal.value) {
        workUnworkedCancelModal.value.show({ temporaryWorkId, year, month });
      }
    };

    const handleDeleteClick = (temporaryWorkId) => {
      paramsIdToDelete.value = temporaryWorkId;
    };

    const handleConfirmDelete = async (temporaryWorkId) => {
      try {
        await deleteTemporaryWork(temporaryWorkId);
        // リストから臨時作業情報を削除
        temporaryWorks.value = temporaryWorks.value.filter(temporaryWork => temporaryWork.id !== temporaryWorkId);
        store.dispatch('setFlashMessage', { message: '臨時作業情報が削除されました', type: 'success' });
        // クエリをルートに反映
        router.push({
          path: '/temporary-works',
          query: getSearchQuery()
        });
      } catch (error) {
        // サーバーからのエラーメッセージを取得
        const errorMessage = error.response?.data?.message || '臨時作業情報削除に失敗しました';
        store.dispatch('setFlashMessage', { message: errorMessage, type: 'danger' });
      }
    };

    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    };

    const formatMonth = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getMonth() + 1}月`;
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    // テーブルの高さの動的な設定
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    // Excel出力処理（臨時作業一覧のデータを.xlsxで出力）
    const exportToExcel = async () => {
      store.dispatch('startGlobalLoading');
      try {
        // ⏬ 全件取得：ページ指定なし + all=true を送信
        const response = await getTemporaryWorks({
          billing_no: searchBillingNo.value,
          year_from: searchYearFrom.value,
          month_from: searchMonthFrom.value,
          year_to: searchYearTo.value,
          month_to: searchMonthTo.value,
          customer_id: searchCustomerName.value,
          building_id: searchBuildingName.value,
          vendor_id: searchVendorName.value,
          user_id: searchUserName.value,
          work_content: searchWorkContent.value,
          billing_flg: searchBillingFlg.value,
          all: true, // 💡これがポイント！
        });

        const allWorks = response.data.temporary_works;

        // ⬇以降は前回の Excel 出力処理と同じです
        const now = new Date();
        const formattedDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

        const data = allWorks.map(work => {
        const vendors = work.t_temporary_work_vendors || [];

        // 最大4件までの発注業者と金額を格納。ない場合は空文字で埋める
        const vendorFields = {};
        for (let i = 0; i < 4; i++) {
          vendorFields[`発注業者${i + 1}`] = vendors[i]?.m_vendor?.vendor_nm || '';
          vendorFields[`発注金額${i + 1}`] = vendors[i]?.request_amount != null
            ? vendors[i].request_amount
            : '';
        }

        return {
          '請求入力No.': work.billing_no,
          '見積書No.': work.quotion_no,
          '建物名称': work.m_building?.building_nm,
          '業務区分': work.m_business_classification?.business_classification_nm,
          '業務内容': work.work_content,
          '適用': work.breakdown,
          '顧客名(請求先)': work.m_customer?.customer_nm,
          '顧客担当者': `${work.m_customer_representative?.in_charge_last_nm || ''} ${work.m_customer_representative?.in_charge_first_nm || ''}`,
          '受注日': formatYearMonthDay(work.order_dt),
          '受注金額(税抜)': work.order_amount,
          ...vendorFields,
          '総務チェック(業者請求確認)': vendors.length > 0
            ? (work.confirm_vendor_billing ? '確認済み' : '未確認')
            : '-',
          '業者請求確認日': vendors.length > 0
            ? formatYearMonthDay(work.vendor_billing_confirm_dt)
            : '-',
          '粗利(%)': work.gross_profit_margin + '%',
          '作業日': formatYearMonthDay(work.work_dt),
          '当社担当者': work.user?.name,
          '請求年月': work.billing_year + '年' + work.billing_month + '月',
          '請求発行依頼(担当者記入)': work.billing_request,
          '総務請求確認日': formatYearMonthDay(work.confirm_billing_dt),
        };
      });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '臨時作業一覧');
        const filename = `臨時作業一覧_${formattedDate}.xlsx`;
        XLSX.writeFile(workbook, filename);
      } catch (error) {
        console.error('Excel出力用データ取得に失敗しました:', error);
      } finally {
        store.dispatch('stopGlobalLoading');
      }
    };

    // ▼▼▼ 追加：クエリパラメータから検索条件を復元 ▼▼▼
    onMounted(() => {
      const q = route.query;

      // 初回表示では今期を使い、明示的にクリアされた空文字はそのまま復元する。
      const queryValue = (key, fallback = '') => (
        Object.prototype.hasOwnProperty.call(q, key) ? q[key] : fallback
      );

      searchBillingNo.value = queryValue('temp_billing_no');
      searchYearFrom.value = queryValue('temp_year_from', searchYearFrom.value);
      searchMonthFrom.value = queryValue('temp_month_from', searchMonthFrom.value);
      searchYearTo.value = queryValue('temp_year_to', searchYearTo.value);
      searchMonthTo.value = queryValue('temp_month_to', searchMonthTo.value);
      searchCustomerName.value = queryValue('temp_customer_id');
      searchBuildingName.value = queryValue('temp_building_id');
      searchVendorName.value = queryValue('temp_vendor_id');
      searchUserName.value = queryValue('temp_user_id');
      searchWorkContent.value = queryValue('temp_work_content');
      searchBillingFlg.value = queryValue('temp_billing_flg');
      orderSortOrder.value = queryValue('temp_order_sort_order', 'asc');
      workSortOrder.value = queryValue('temp_work_sort_order');

      // ページ番号も保持
      currentPage.value = q.temp_page ? Number(q.temp_page) : 1;

      // 復元後に該当ページで検索実行
      fetchTemporaryWorks(currentPage.value);
    });

    return {
      title,
      userId,
      userRole,
      isAdmin,
      route,
      customers,
      buildings,
      vendors,
      users,
      temporaryWorks,
      totalOrderAmount,
      totalRequestAmount,
      totalGrossProfitMargin,
      years,
      months,
      targetYear,
      targetMonth,
      getSearchQuery,
      paramsIdToDelete,
      handleDeleteClick,
      handleConfirmDelete,
      fetchTemporaryWorks,
      generalAffairsVendorCheckModal,
      openGeneralAffairsVendorCheckModal,
      generalAffairsVendorCancelModal,
      openGeneralAffairsVendorCancelModal,
      workCompleteCheckModal,
      openWorkCompleteCheckModal,
      workCompleteCancelModal,
      openWorkCompleteCancelModal,
      workUnworkedCancelModal,
      openWorkUnworkedCancelModal,
      errorMessages,
      searchBillingNo,
      searchYearFrom,
      searchMonthFrom,
      searchYearTo,
      searchMonthTo,
      searchCustomerName,
      searchBuildingName,
      searchVendorName,
      searchUserName,
      searchWorkContent,
      searchBillingFlg,
      filteredYearsTo,
      filteredMonthsTo,
      currentPage,
      totalPages,
      handleChangePage,
      searchTemporaryWorks,
      searchResetTemporaryWorks,
      formatYearMonthDay,
      formatMonth,
      formatCurrency,
      isSearchExpanded,
      topFixArea,
      tableWrapper,
      paginationContainer,
      orderSortOrder,
      workSortOrder,
      toggleOrderSortOrder,
      toggleSortOrder,
      handleCopyClick,
      exportToExcel
    };
  }
};
</script>

<style scoped>
/* 検索スライド開閉のトランジション */
.accordion-enter-active,
.accordion-leave-active {
  overflow: hidden;
}

.temp-total-amount-area th,
.temp-total-amount-area td{
  font-size: 20px;
  padding: 8px 16px;
}

.temp-total-amount-area th{
  background-color: #e9f4f9;
}
.temporaryWork-item {
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.temporaryWork-item:hover {
  background-color: #f5f5f5;
}

.table-wrapper table{
  background: #FFFFFF;
  border: 1px solid #BBBBBB;
  margin-bottom: 0;
  height: 100%;
}

.table-wrapper thead th {
  position: sticky;
  top: 0;
  background: #003366 !important; /* 背景色を設定し、スクロール中に背景が透けるのを防ぐ */
  color: #fff !important;
  z-index: 55;
}

.table-wrapper th.sticky-left,
.table-wrapper th.sticky-left-second,
.table-wrapper th.sticky-left-third{
  z-index: 100 !important;
}

/* 横スクロール時に固定するカラム */
.table-wrapper th.sticky-left, td.sticky-left {
  position: sticky;
  left: 0px;
  min-width: 240px;
  width: 240px;
  max-width: 240px;
  background: #f0f8ff;
  box-shadow: inset -1px 0 #BBBBBB; /* 内側にボーダー風の影をつける */
}

.table-wrapper th.sticky-left-second, td.sticky-left-second {
  position: sticky;
  left: 240px;
  min-width: 120px;
  width: 120px;
  max-width: 120px;
  background: #f0f8ff;
  box-shadow: inset -1px 0 #BBBBBB; /* 内側にボーダー風の影をつける */
}

.table-wrapper th.sticky-left-third, td.sticky-left-third {
  position: sticky;
  left: 360px;
  min-width: 120px;
  width: 120px;
  max-width: 120px;
  background: #f0f8ff;
  box-shadow: inset -1px 0 #BBBBBB; /* 内側にボーダー風の影をつける */
}

td.sticky-left{ z-index: 50; }
td.sticky-left-second{ z-index: 49 }
td.sticky-left-third{ z-index: 48 }

.table-wrapper table th,
.table-wrapper table td{
  font-size: 12px;
}

.table-wrapper tbody tr.unworked-row td {
  background-color: #efefef;
  color: #6c757d;
}

.table-wrapper tbody tr.unworked-row td.sticky-left,
.table-wrapper tbody tr.unworked-row td.sticky-left-second,
.table-wrapper tbody tr.unworked-row td.sticky-left-third {
  background-color: #e4e4e4 !important;
}

.table-wrapper tbody tr.unworked-row .vendor-info-table th {
  background-color: #dddddd;
  color: #6c757d;
}

.vendor-info-table{
  width: 100%;
  border: none !important;
}
.vendor-info-table tr{
  border-bottom: 1px solid #BBBBBB;
}
.vendor-info-table tr:last-child{
  border-bottom: none !important;
}
.vendor-info-table th,
.vendor-info-table td{
  padding: 4px 8px;
}
.vendor-info-table th{
  max-width: 240px;
  width: 240px;
  min-width: 240px;
  border-right: 1px solid #BBBBBB;
  background-color: #e9f4f9;
  font-weight: normal;
}
</style>
